import axios from "axios";
import GetUploadUrlRequest from "../models/serviceModels/GetUploadUrlRequest";
import GetUploadUrlResponse from "../models/serviceModels/GetUploadUrlResponse";
import { UploadFileRequest } from "../models/serviceModels/UploadFileRequest";
import GetDownloadUrlRequest from "../models/serviceModels/GetDownloadUrlRequest";
import GetDownloadUrlResponse from "../models/serviceModels/GetDownloadUrlResponse";
import DeleteFileUploadRequest from "../models/serviceModels/DeleteFileUploadRequest";
import DeleteFileUploadResponse from "../models/serviceModels/DeleteFileUploadResponse";
import UpsertClientFilesRequest from "../models/serviceModels/UpsertClientFilesRequest";
import ClientFile from "../models/serviceModels/ClientFile";
import * as fileScanStatus from "../constants/fileScanStatus";
import * as fileUploadStatus from "../constants/fileUploadStatus";
import { FileUpload } from "../models/serviceModels/FileUpload";
import { ClientRelatedFile } from "../models/serviceModels/ClientRelatedFile";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const getPublicHeaders = (publicFormId?: string, publicFollowUpToken?: string): Record<string, string> => {
    if (publicFormId && publicFollowUpToken) {
        throw new Error('Only one public file authorization context may be supplied');
    }

    if (publicFollowUpToken) {
        return { 'x-public-follow-up-token': publicFollowUpToken };
    }

    if (publicFormId) {
        return { 'x-public-form-id': publicFormId };
    }

    return {};
};

const getUploadUrl = async (request: GetUploadUrlRequest): Promise<GetUploadUrlResponse> => {
    const headers = getPublicHeaders(request.publicFormId, request.publicFollowUpToken);
    const response = await axios.post(`${baseUrl}/files/uploads`, request, { headers });
    return {
        ...response.data,
        fileName: request.fileName
    };
};

const getFileUpload = async (fileId: string, publicFormId?: string, publicFollowUpToken?: string): Promise<FileUpload> => {
    const headers = getPublicHeaders(publicFormId, publicFollowUpToken);
    const response = await axios.get(`${baseUrl}/files/${fileId}/uploads`, { headers });
    return response.data;
};

const uploadFile = async (request: UploadFileRequest): Promise<string> => {
    const headers: any = {};
    if (request.publicFormId) {
        headers['x-public-form-id'] = request.publicFormId;
    }
    await axios.put(request.url, request.file, {
        cancelToken: request.cancellationToken?.token,
        headers
    });

    return request.fileId;
};

const getDownloadUrl = async (request: GetDownloadUrlRequest): Promise<GetDownloadUrlResponse> => {
    const headers = getPublicHeaders(request.publicFormId, request.publicFollowUpToken);
    const response = await axios.post(`${baseUrl}/files/downloads`, request, { headers });
    return response.data;
};

const deleteFileUpload = async (request: DeleteFileUploadRequest): Promise<DeleteFileUploadResponse> => {
    const headers = getPublicHeaders(request.publicFormId, request.publicFollowUpToken);
    const response = await axios.delete(`${baseUrl}/files/${request.fileId}/uploads`, { headers });
    return response.data;
};

const upsertClientFiles = async (request: UpsertClientFilesRequest): Promise<ClientFile[]> => {
    const response = await axios.post(`${baseUrl}/files/clients/${request.clientId}`, {
        files: request.files
    });
    return response.data;
};

const getClientFiles = async (clientId: string): Promise<ClientFile[]> => {
    const response = await axios.get(`${baseUrl}/files/clients/${clientId}`);
    return response.data.map((d: any) => {
        return {
            fileId: d.fileId,
            id: d.id,
            name: d.name,
            status: d.status === fileScanStatus.INFECTED ? fileUploadStatus.INFECTED :
                d.status === fileScanStatus.CLEAN ? fileUploadStatus.UPLOADED :
                fileUploadStatus.SCANNING,
            created: d.created
        }
    });
};

const getClientRelatedFiles = async (clientId: string, sortBy?: string, sortDirection?: 'asc' | 'desc'): Promise<ClientRelatedFile[]> => {
    const response = await axios.get(`${baseUrl}/files/clients/${clientId}/related`, { params: { sortBy, sortDirection } });
    return response.data;
};

export {
    getUploadUrl,
    uploadFile,
    getDownloadUrl,
    deleteFileUpload,
    upsertClientFiles,
    getClientFiles,
    getFileUpload,
    getClientRelatedFiles
}
